import { useSyncExternalStore } from 'react';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { RoiIntensityStats } from '~/engine/data/structure/RegionOfInterest';
import { useDomainStore } from '~/hooks/domain/useDomainStore';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';
import type { RoiId } from '~/domain/changes/ids';

/**
 * The intensity statistics of an ROI and the image they were read from, which
 * names their unit. Undefined for a point or empty ROI, with no image, while
 * the image is still loading, or without a `roiId`, which computes
 * nothing.
 *
 * Re-reads on every domain change, like `useRoiStatistics`; the engine caches
 * the result, so only a change to this ROI re-renders the panel. Slices
 * arrive outside the domain store, so the image's loading progress is
 * watched too, and the figures appear as soon as the last slice lands.
 *
 * The ROI is read from the edit target's Environment, so on the grid the
 * figures come from the row that holds the structure set.
 */
export function useRoiIntensityStatistics(
    roiId: RoiId | undefined
): RoiIntensityStats | undefined {
    const { environment: env } = useActiveEditTarget();
    const store = useDomainStore();
    const referencedImage = env?.structureSet?.parent.image ?? undefined;
    const { isComplete } = useImageStreamProgress(referencedImage);
    return useSyncExternalStore(store.subscribe, () =>
        isComplete && roiId !== undefined
            ? env?.structureSet?.getRoiIntensityStatistics(roiId)
            : undefined
    );
}
